import { useRef, useState } from "react";
import { Button } from "antd";
import { CloudUploadOutlined } from "@ant-design/icons";
import "./ProfileVisualAssetsSection.scss";

type AssetSectionKey = "logo" | "cover" | "interior" | "exterior" | "foodDrink";

const SECTIONS: { key: AssetSectionKey; label: string; multi: boolean }[] = [
  { key: "logo", label: "Logo", multi: false },
  { key: "cover", label: "Cover Image", multi: false },
  { key: "interior", label: "Interior Photos", multi: true },
  { key: "exterior", label: "Exterior Photos", multi: true },
  { key: "foodDrink", label: "Food and Drink Photos", multi: true },
];

interface UploadedFile {
  id: string;
  name: string;
  url: string;
}

type AssetState = Record<AssetSectionKey, UploadedFile[]>;

const EMPTY: AssetState = {
  logo: [],
  cover: [],
  interior: [],
  exterior: [],
  foodDrink: [],
};

export function ProfileVisualAssetsSection() {
  const [assets, setAssets] = useState<AssetState>(EMPTY);

  function addFiles(key: AssetSectionKey, files: FileList | null, multi: boolean) {
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    const accepted = multi ? list : list.slice(0, 1);
    const next: UploadedFile[] = accepted.map((f, i) => ({
      id: `${Date.now()}-${i}-${f.name}`,
      name: f.name,
      url: URL.createObjectURL(f),
    }));
    setAssets((s) => ({
      ...s,
      [key]: multi ? [...s[key], ...next] : next,
    }));
  }

  return (
    <div className="profile-visual-assets">
      <h3 className="pva-title">Visual Assets</h3>

      {SECTIONS.map((s) => (
        <section key={s.key} className="pva-group">
          <span className="pva-group-label">{s.label}</span>
          {assets[s.key].length > 0 && (
            <div className={`pva-thumbs${s.multi ? " multi" : " single"}`}>
              {assets[s.key].map((f) => (
                <div className="pva-thumb" key={f.id}>
                  <img src={f.url} alt={f.name} />
                </div>
              ))}
            </div>
          )}
          <AssetUploader
            multi={s.multi}
            onFiles={(files) => addFiles(s.key, files, s.multi)}
          />
        </section>
      ))}
    </div>
  );
}

interface AssetUploaderProps {
  multi: boolean;
  onFiles: (files: FileList | null) => void;
}

function AssetUploader({ multi, onFiles }: AssetUploaderProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    onFiles(e.dataTransfer.files);
  }

  return (
    <div
      className={`pva-dropzone${dragging ? " dragging" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <CloudUploadOutlined className="pva-cloud-icon" />
      <p className="pva-dropzone-text">Drag and drop images here or</p>
      <Button
        className="pva-choose-btn"
        onClick={() => inputRef.current?.click()}
      >
        Choose Media
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multi}
        style={{ display: "none" }}
        onChange={(e) => {
          onFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
